
import React, { useState, useMemo } from 'react';
import { MdsItem, Status, Millsheet, ItemDetail } from '../types';
import ItemDetailModal from './ItemDetailModal';

interface MdsTableProps {
  items: MdsItem[];
  onUpdateItem: (updatedItem: MdsItem, originalPartNumber?: string) => void;
  onDeleteItem: (partNumber: string, itemNo?: number) => void;
  onUpdateDetails: (partNumber: string, details: ItemDetail) => void;
  initialSearchTerm?: string;
}

const MdsTable: React.FC<MdsTableProps> = ({ items, onUpdateItem, onDeleteItem, onUpdateDetails, initialSearchTerm }) => {
  const [editingNo, setEditingNo] = useState<number | null>(null);
  const [editState, setEditState] = useState<MdsItem | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [originalPn, setOriginalPn] = useState<string | null>(null);
  const [detailItem, setDetailItem] = useState<MdsItem | null>(null);

  React.useEffect(() => {
    if (initialSearchTerm !== undefined) {
      setSearchTerm(initialSearchTerm);
    }
  }, [initialSearchTerm]);

  const startEdit = (item: MdsItem) => {
    setEditingNo(item.no);
    setEditState({ ...item });
    setOriginalPn(item.part_number_customer);
  };

  const handleSave = () => {
    if (editState && editState.part_number_customer) {
      onUpdateItem(editState, originalPn || undefined);
      setEditingNo(null);
      setEditState(null);
      setOriginalPn(null);
    }
  };

  const handleCancel = () => {
    setEditingNo(null);
    setEditState(null);
    setOriginalPn(null);
  };

  const filteredItems = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return items.filter(i => 
      i.part_number_customer.toLowerCase().includes(term) || 
      i.part_name_customer.toLowerCase().includes(term) ||
      (i.imdsData?.casNo && i.imdsData.casNo.toLowerCase().includes(term)) ||
      (i.imdsData?.substanceName && i.imdsData.substanceName.toLowerCase().includes(term))
    ).sort((a, b) => a.no - b.no);
  }, [items, searchTerm]);

  const updateEditField = (field: keyof MdsItem, value: any) => {
    if (editState) {
      setEditState({ ...editState, [field]: value });
    }
  };

  const updateImdsField = (field: string, value: any) => {
    if (editState) {
      setEditState({
        ...editState,
        imdsData: {
          ...editState.imdsData,
          [field]: value
        }
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="relative w-full max-w-md">
          <svg className="w-5 h-5 absolute left-3 top-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            placeholder="Filter parts..."
            className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none dark:text-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
          {filteredItems.length} Items Found
        </div>
      </div>

      <div className="overflow-x-auto border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm bg-white dark:bg-slate-800">
        <table className="w-full text-center border-collapse text-xs min-w-[3000px]">
          <thead className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700 text-slate-500 font-medium uppercase tracking-wider">
            <tr className="divide-x divide-slate-200 dark:divide-slate-700">
              <th className="p-3 w-12" rowSpan={2}>No</th>
              <th className="p-3 w-12" rowSpan={2}>Lvl</th>
              <th className="p-3 text-left w-48" rowSpan={2}>Part Number (Cust)</th>
              <th className="p-3 text-left w-64" rowSpan={2}>Part Name (Cust)</th>
              
              {/* IMDS Data Columns */}
              <th className="p-3 w-48" rowSpan={2}>Description</th>
              <th className="p-3 w-48" rowSpan={2}>Article Name</th>
              <th className="p-3 w-48" rowSpan={2}>Name</th>
              <th className="p-3 w-48" rowSpan={2}>Substance Name</th>
              <th className="p-3 w-32" rowSpan={2}>Part/Item No.</th>
              <th className="p-3 w-32" rowSpan={2}>Item-/Mat.-No.</th>
              <th className="p-3 w-32" rowSpan={2}>Material-No.</th>
              <th className="p-3 w-32" rowSpan={2}>CAS No.</th>
              <th className="p-3 w-32" rowSpan={2}>IMDS ID/Ver</th>
              <th className="p-3 w-24" rowSpan={2}>Qty</th>
              <th className="p-3 w-24" rowSpan={2}>Weight (g)</th>
              <th className="p-3 w-24" rowSpan={2}>Portion (%)</th>
              <th className="p-3 w-24" rowSpan={2}>Portion Range</th>
              <th className="p-3 w-24" rowSpan={2}>Classif.</th>
              <th className="p-3 w-24" rowSpan={2}>GADSL/SVHC</th>
              <th className="p-3 w-32" rowSpan={2}>Parts Marking</th>
              <th className="p-3 w-32" rowSpan={2}>Source Material</th>
              <th className="p-3 w-24" rowSpan={2}>App ID</th>

              <th className="p-3 w-24" rowSpan={2}>Status</th>
              <th className="p-3 w-16" rowSpan={2}>UOM</th>
              <th className="p-3" colSpan={8}>Weight (gr) per Level</th>
              <th className="p-3 w-16" rowSpan={2}>MS</th>
              <th className="p-3 text-left w-40" rowSpan={2}>Manufacturer</th>
              <th className="p-3 w-32" rowSpan={2}>Actions</th>
            </tr>
            <tr className="divide-x divide-slate-200 dark:divide-slate-700 text-[10px] bg-slate-100/50 dark:bg-slate-800">
              {[0,1,2,3,4,5,6,7].map(l => (
                <th key={l} className="w-12 p-1">{l}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {filteredItems.map((item) => {
              const isEditing = editingNo === item.no;
              const d = isEditing ? editState! : item;
              const imds = d.imdsData || {};

              return (
                <tr key={item.no} className={`divide-x divide-slate-100 dark:divide-slate-700 transition-colors ${isEditing ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'}`}>
                  <td className="p-3 text-slate-500 font-mono">{d.no}</td>
                  <td className="p-3 font-medium text-slate-900 dark:text-white">
                    {isEditing ? <input type="number" min="0" max="7" className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1" value={d.level} onChange={e => updateEditField('level', parseInt(e.target.value) || 0)} /> : d.level}
                  </td>
                  
                  <td className="p-3 text-left font-mono font-medium text-slate-900 dark:text-white">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={d.part_number_customer} onChange={e => updateEditField('part_number_customer', e.target.value.toUpperCase())} /> : d.part_number_customer}
                  </td>
                  <td className="p-3 text-left text-slate-700 dark:text-slate-300 uppercase">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={d.part_name_customer} onChange={e => updateEditField('part_name_customer', e.target.value.toUpperCase())} /> : d.part_name_customer}
                  </td>

                  {/* IMDS Data Fields */}
                  <td className="p-3 text-left text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={imds.description}>
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.description || ''} onChange={e => updateImdsField('description', e.target.value)} /> : (imds.description || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={imds.articleName}>
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.articleName || ''} onChange={e => updateImdsField('articleName', e.target.value)} /> : (imds.articleName || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={imds.name}>
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.name || ''} onChange={e => updateImdsField('name', e.target.value)} /> : (imds.name || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-600 dark:text-slate-400 truncate max-w-[150px]" title={imds.substanceName}>
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.substanceName || ''} onChange={e => updateImdsField('substanceName', e.target.value)} /> : (imds.substanceName || '-')}
                  </td>
                  <td className="p-3 text-left font-mono text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.partItemNo || ''} onChange={e => updateImdsField('partItemNo', e.target.value)} /> : (imds.partItemNo || '-')}
                  </td>
                  <td className="p-3 text-left font-mono text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.itemMatNo || ''} onChange={e => updateImdsField('itemMatNo', e.target.value)} /> : (imds.itemMatNo || '-')}
                  </td>
                  <td className="p-3 text-left font-mono text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.materialNo || ''} onChange={e => updateImdsField('materialNo', e.target.value)} /> : (imds.materialNo || '-')}
                  </td>
                  <td className="p-3 text-left font-mono text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.casNo || ''} onChange={e => updateImdsField('casNo', e.target.value)} /> : (imds.casNo || '-')}
                  </td>
                  <td className="p-3 text-left font-mono text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.imdsIdVersion || ''} onChange={e => updateImdsField('imdsIdVersion', e.target.value)} /> : (imds.imdsIdVersion || '-')}
                  </td>
                  <td className="p-3 font-mono text-slate-700 dark:text-slate-300">
                     {isEditing ? <input type="number" className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1" value={imds.quantity || 0} onChange={e => updateImdsField('quantity', parseFloat(e.target.value) || 0)} /> : (imds.quantity || '-')}
                  </td>
                  <td className="p-3 font-mono text-slate-700 dark:text-slate-300">
                     {isEditing ? <input type="number" step="any" className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1" value={imds.weight || 0} onChange={e => updateImdsField('weight', parseFloat(e.target.value) || 0)} /> : (imds.weight || '-')}
                  </td>
                  <td className="p-3 font-mono text-slate-700 dark:text-slate-300">
                     {isEditing ? <input type="number" step="any" className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1" value={imds.portion || 0} onChange={e => updateImdsField('portion', parseFloat(e.target.value) || 0)} /> : (imds.portion || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.portionRange || ''} onChange={e => updateImdsField('portionRange', e.target.value)} /> : (imds.portionRange || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.classification || ''} onChange={e => updateImdsField('classification', e.target.value)} /> : (imds.classification || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.gadslSvhc || ''} onChange={e => updateImdsField('gadslSvhc', e.target.value)} /> : (imds.gadslSvhc || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.partsMarking || ''} onChange={e => updateImdsField('partsMarking', e.target.value)} /> : (imds.partsMarking || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.sourceOfMaterial || ''} onChange={e => updateImdsField('sourceOfMaterial', e.target.value)} /> : (imds.sourceOfMaterial || '-')}
                  </td>
                  <td className="p-3 text-left text-slate-500">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={imds.applicationId || ''} onChange={e => updateImdsField('applicationId', e.target.value)} /> : (imds.applicationId || '-')}
                  </td>

                  <td className="p-3">
                    {isEditing ? (
                      <select 
                        className="bg-white dark:bg-slate-700 border border-slate-300 rounded px-1 py-1 text-xs outline-none"
                        value={d.status}
                        onChange={e => updateEditField('status', e.target.value as Status)}
                      >
                        <option value={Status.NEW}>{Status.NEW}</option>
                        <option value={Status.MP}>{Status.MP}</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${d.status === Status.NEW ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{d.status}</span>
                    )}
                  </td>
                  <td className="p-3 text-slate-600 dark:text-slate-400">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1" value={d.uom} onChange={e => updateEditField('uom', e.target.value.toUpperCase())} /> : d.uom}
                  </td>

                  {[0,1,2,3,4,5,6,7].map(l => (
                    <td key={l} className={`p-1 font-mono text-[10px] ${d.level === l ? 'bg-blue-50/50 dark:bg-blue-900/10' : 'text-slate-300 dark:text-slate-700'}`}>
                      {d.level === l ? (
                        isEditing ? (
                          <input 
                            type="number" 
                            step="any"
                            className="w-full bg-white dark:bg-slate-700 text-center outline-none border border-slate-300 rounded py-1"
                            value={d.berat_level}
                            onChange={e => updateEditField('berat_level', parseFloat(e.target.value) || 0)}
                          />
                        ) : (
                          <span className="text-slate-900 dark:text-white font-medium">{d.berat_level.toFixed(2)}</span>
                        )
                      ) : '-'}
                    </td>
                  ))}

                  <td className="p-3">
                    {isEditing ? (
                      <select 
                        className="bg-white dark:bg-slate-700 border border-slate-300 rounded px-1 py-1 text-xs outline-none"
                        value={d.millsheet}
                        onChange={e => updateEditField('millsheet', e.target.value as Millsheet)}
                      >
                        <option value={Millsheet.Y}>Y</option>
                        <option value={Millsheet.N}>N</option>
                        <option value={Millsheet.X}>X</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-[10px] font-medium ${d.millsheet === Millsheet.Y ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}`}>
                        {d.millsheet}
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-left text-slate-600 dark:text-slate-400 truncate max-w-[150px] uppercase font-medium">
                    {isEditing ? <input className="w-full bg-white dark:bg-slate-700 border border-slate-300 rounded px-2 py-1 outline-none" value={d.manufacturer || ''} onChange={e => updateEditField('manufacturer', e.target.value.toUpperCase())} /> : (d.manufacturer || '-')}
                  </td>

                  <td className="p-3 flex gap-2 justify-center">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <button onClick={handleSave} className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors" title="Save">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                        </button>
                        <button onClick={handleCancel} className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors" title="Cancel">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    ) : (
                      <>
                        <button onClick={() => setDetailItem(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Item Detail">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                        </button>
                        <button onClick={() => startEdit(item)} className="p-1.5 text-slate-600 hover:bg-slate-100 rounded transition-colors" title="Edit row">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button onClick={() => onDeleteItem(item.part_number_customer)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete item">
                           <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {detailItem && (
        <ItemDetailModal 
          item={detailItem} 
          onClose={() => setDetailItem(null)} 
          onSave={(details) => {
            onUpdateDetails(detailItem.part_number_customer, details);
            setDetailItem(null);
          }} 
        />
      )}
    </div>
  );
};

export default MdsTable;
